import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, from, split } from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { Store } from 'redux';
import { StoreType } from '@tock/shared/utils/connect';
import { createClient } from 'graphql-ws';
import { getBusinessHeader } from '@tock/shared/utils/AjaxActions';
import { getMainDefinition, relayStylePagination } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { showError } from '@tock/toast';
import AppSettings from 'settings/AppSettings';
import InMemoryCacheConfig from 'generated/graphql';
import _ from 'lodash';
import fetch from 'cross-fetch';

// Ping the server every 20 seconds to make sure the connection is still alive
// Set to same ping interval as WebsocketBase
const KEEP_ALIVE_TIME_MS = 20_000;

// After sending a ping, wait up to 60 seconds to receive a pong from the server
// before assuming the connection is dead and starting a new one
// Set to same Pong timeout as WebsocketBase
const WAIT_FOR_PONG_MS = 60_000;

// After all subscriptions are complete, wait 10 seconds before closing the
// connection. This allows a user quickly hopping between realtime and
// non-realtime pages to re-use a connection rather than creating a new one.
const CONNECTION_CLOSE_DEBOUNCE_MS = 10_000;

/**
 * By default, the graphql-ws library sends a GraphQL-specific protocol into the
 * WebSocket conection, which gets sent as a header. Undertow doesn't know what
 * to do with that header and ignores the request entirely. This little hack
 * gets around that issue by not sending up any protocols.
 */
class ProtocolFreeWebSocket extends WebSocket {
  constructor(url: string, protocols: unknown) {
    super(url, []);
  }
}

export function createLinkChain({
  jwtToken,
  activeBusinessId,
  activeBusinessGroupId,
  store,
}: {
  jwtToken: string;
  activeBusinessId: number;
  activeBusinessGroupId: number;
  store: Store;
}) {
  const apolloAuth = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        ..._.omitBy(getBusinessHeader(store.getState().app), _.isUndefined),
      },
    }));
    return forward(operation);
  });

  const apolloError = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors?.length) {
      store.dispatch(showError(graphQLErrors.map((error) => error.message).join('\n')));
    }

    if (networkError) {
      store.dispatch(showError('Failed to connect, check your internet and try again.'));
    }
  });

  const apolloHttpLink = new HttpLink({
    uri: `${AppSettings.API_BASE}/graphql`,
    credentials: 'include',
    includeExtensions: true,
    fetch: (uri, options) => {
      let opName = 'UnknownOperation';
      if (typeof options?.body === 'string') {
        const { operationName } = JSON.parse(options?.body);
        if (operationName) {
          opName = operationName;
        }
      }
      return fetch(`${uri}/${opName}`, options);
    },
  });

  let activeWebSocket: WebSocket;
  let timeout: ReturnType<typeof setTimeout>;

  const apolloWebsocketLink = new GraphQLWsLink(
    createClient({
      url: `${AppSettings.WSS_BASE}/graphql/${activeBusinessGroupId}/${activeBusinessId}?auth=${jwtToken}`,
      webSocketImpl: ProtocolFreeWebSocket,
      keepAlive: KEEP_ALIVE_TIME_MS,
      lazyCloseTimeout: CONNECTION_CLOSE_DEBOUNCE_MS,
      retryAttempts: 10, // Uses randomized exponential backoff. A value of 10 gives up after ~17 minutes.
      on: {
        connected: (socket: unknown) => {
          if (socket instanceof WebSocket) {
            activeWebSocket = socket;
          }
        },
        ping: (received: boolean) => {
          const sent = !received;
          if (sent) {
            timeout = setTimeout(() => {
              if (activeWebSocket?.readyState === WebSocket.OPEN) {
                activeWebSocket.close(4408, 'Request Timeout');
              }
            }, WAIT_FOR_PONG_MS);
          }
        },
        pong: (received: boolean) => {
          if (received && timeout) {
            clearTimeout(timeout);
          }
        },
      },
      shouldRetry: (errOrCloseEvent) => {
        // Retry if there's an Event, which usually indicates something connection-related.
        // Consider an issue fatal (and don't retry) if the websocket server explicitly sends an error.
        return errOrCloseEvent instanceof Event;
      },
    })
  );

  // Use either the standard HTTP link or the WebSocket link based on the type
  // of query that's getting sent.
  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    apolloWebsocketLink,
    apolloHttpLink
  );

  return from([apolloAuth, apolloError, splitLink]);
}

export function initializeApolloClient(store: Store<StoreType>) {
  const { app } = store.getState();

  const apolloClient = new ApolloClient({
    cache: new InMemoryCache({
      ...InMemoryCacheConfig,
      typePolicies: {
        Query: {
          fields: {
            businessesPaginated: relayStylePagination(['unlisted', 'query']),
            businessGroupsPaginated: relayStylePagination(['query']),
          },
        },
      },
    }),
  });

  apolloClient.setLink(
    createLinkChain({
      activeBusinessId: app.activeBusiness?.id ?? -1,
      activeBusinessGroupId: app.activeBusinessGroup?.id ?? -1,
      jwtToken: app.jwtToken,
      store,
    })
  );

  return apolloClient;
}
